// Pure helpers of the board UI: tints, labels, grouping of cards and the
// drop-position maths of drag and drop.

import type {
  BoardCard,
  BoardColumn,
  BoardResponse,
  PullResult,
  QueueLane,
  Severity,
  StreamColor,
  StreamDefinition,
  StreamOrderRule,
} from '@goblic/shared'

/** Full class names so Tailwind sees them (never build these dynamically). */
export const STREAM_COLOR_CLASS: Record<StreamColor, { dot: string; soft: string; text: string }> = {
  moss: { dot: 'bg-moss', soft: 'bg-moss-soft', text: 'text-moss' },
  info: { dot: 'bg-info', soft: 'bg-info-soft', text: 'text-info' },
  signal: { dot: 'bg-signal', soft: 'bg-signal-soft', text: 'text-signal' },
  danger: { dot: 'bg-danger', soft: 'bg-danger-soft', text: 'text-danger' },
  ok: { dot: 'bg-ok', soft: 'bg-ok-soft', text: 'text-ok' },
  ink: { dot: 'bg-ink-3', soft: 'bg-sunken', text: 'text-ink-2' },
}

export const SEVERITY_CLASS: Record<Severity, string> = {
  S1: 'bg-danger-soft text-danger',
  S2: 'bg-signal-soft text-signal',
  S3: 'bg-info-soft text-info',
  S4: 'bg-sunken text-ink-3',
}

export const ORDER_LABEL: Record<StreamOrderRule, string> = {
  manual: 'manual rank',
  severity_age: 'severity, then age',
  priority_age: 'priority, then age',
}

export const QUEUE_LANE_LABEL: Record<QueueLane, string> = {
  inbox: 'Inbox',
  backlog: 'Backlog',
  ready: 'Ready',
}

export const PULL_BLOCKED_TEXT: Record<NonNullable<BoardResponse['pullBlockedReason']>, string> = {
  paused: 'This stream is paused: nothing is started.',
  budget_exhausted: 'The weekly budget is used up: nothing new is started.',
  wip_full: 'The WIP limit is reached: the next task starts when something finishes.',
  auto_pull_off: 'Auto-pull is off: start tasks with “Start next” or by dragging them into the flow.',
}

const PULL_STOP_TEXT: Record<NonNullable<PullResult['reason']>, string> = {
  no_stream: 'This stream no longer exists.',
  paused: 'The stream is paused.',
  auto_pull_off: 'Auto-pull is off.',
  budget_exhausted: 'The weekly budget is used up.',
  wip_full: 'The WIP limit is reached.',
  empty: 'Nothing is waiting to be started.',
}

/** One line for the result of "Start next". */
export function describePullResult(result: PullResult): { tone: 'success' | 'info' | 'error'; text: string } {
  const started = result.started.map((s) => s.ticketKey ?? 'a task')
  const failed = result.failed.map((f) => `${f.ticketKey ?? 'a task'} (${f.reason})`)
  const why = result.reason ? PULL_STOP_TEXT[result.reason] : ''
  if (failed.length > 0) {
    return { tone: 'error', text: `Couldn’t start ${failed.join(', ')}.${why ? ` ${why}` : ''}` }
  }
  if (started.length > 0) {
    return { tone: 'success', text: `Started ${started.join(', ')}.${why ? ` ${why}` : ''}` }
  }
  return { tone: 'info', text: why || 'Nothing was started.' }
}

export const isLaneColumn = (column: Pick<BoardColumn, 'kind'>): boolean => column.kind === 'lane'
export const isStateColumn = (column: Pick<BoardColumn, 'kind'>): boolean =>
  column.kind === 'agent' || column.kind === 'human' || column.kind === 'decision'

/** Header tint: people's turn = signal, decisions = info, everything else neutral. */
/** A stream that works like the bug template: triaged by severity rather than ranked by hand. */
export const isBugStream = (stream: StreamDefinition): boolean =>
  ['bug', 'bugs'].includes(stream.workflowKey) || stream.order === 'severity_age' || stream.expedite != null

/** Caption of the board's create button when the stream sets none. */
export const defaultNewTaskLabel = (stream: StreamDefinition): string =>
  isBugStream(stream) ? 'Report bug' : '+ New task'

/** Caption of the board's create button: the stream's own, else derived from it. */
export const newTaskLabelOf = (stream: StreamDefinition): string =>
  stream.newTaskLabel?.trim() || defaultNewTaskLabel(stream)

export function columnTint(column: Pick<BoardColumn, 'kind'>): { header: string; body: string } {
  if (column.kind === 'human') return { header: 'bg-signal-soft text-signal', body: 'bg-signal-soft/30' }
  if (column.kind === 'decision') return { header: 'bg-info-soft text-info', body: 'bg-info-soft/30' }
  return { header: 'bg-sunken text-ink-2', body: 'bg-sunken/40' }
}

/** The column a not-yet-started task enters when dragged into the flow: the first workflow column. */
export function initialStateColumn(columns: BoardColumn[]): BoardColumn | null {
  return columns.find(isStateColumn) ?? null
}

/** Cards per column id, in the order the API sent them (the API sorts lanes by rank / order rule). */
export function cardsByColumn(cards: BoardCard[]): Map<string, BoardCard[]> {
  const map = new Map<string, BoardCard[]>()
  for (const card of cards) {
    const list = map.get(card.columnId)
    if (list) list.push(card)
    else map.set(card.columnId, [card])
  }
  return map
}

/** What is being dragged. */
export interface DragInfo {
  taskId: string
  fromColumnId: string
}

/** The slot a card would drop into: an index in the column's cards without the dragged card. */
export interface DropSlot {
  columnId: string
  index: number
}

/** Whether a card can be picked up: queued tasks and tasks in a workflow state (not Done). */
export function isDraggable(card: BoardCard): boolean {
  if (card.columnId === 'done') return false
  return card.columnId.startsWith('lane:') || card.columnId.startsWith('state:')
}

/**
 * Index of the slot under the pointer: before the hovered card when the
 * pointer is in its upper half, after it otherwise. `siblings` excludes the
 * dragged card.
 */
export function slotIndexFor(siblings: BoardCard[], hoveredId: string, pointerInUpperHalf: boolean): number {
  const at = siblings.findIndex((c) => c.id === hoveredId)
  if (at < 0) return siblings.length
  return pointerInUpperHalf ? at : at + 1
}

/** afterTaskId / beforeTaskId of a slot (the neighbours that end up above and below). */
export function neighboursOfSlot(
  siblings: BoardCard[],
  index: number,
): { afterTaskId: string | null; beforeTaskId: string | null } {
  return {
    afterTaskId: siblings[index - 1]?.id ?? null,
    beforeTaskId: siblings[index]?.id ?? null,
  }
}

/** "2d in Ready": time since the card entered its column. */
export function timeInColumn(enteredAt: string, now: number = Date.now()): string {
  const ms = Math.max(0, now - new Date(enteredAt).getTime())
  const minutes = Math.floor(ms / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  return `${Math.floor(hours / 24)}d`
}

/** What a drop of a card on a column does; null when it isn't allowed. */
export type DropAction =
  | { kind: 'queue'; lane: NonNullable<BoardColumn['lane']> }
  | { kind: 'submit' }
  | { kind: 'transition'; toState: string }
  /** Done folds several terminal states: the person picks which one the task ends in. */
  | { kind: 'finish'; states: NonNullable<BoardColumn['terminalStates']> }

export function dropActionFor(card: BoardCard, column: BoardColumn, columns: BoardColumn[]): DropAction | null {
  const fromLane = card.columnId.startsWith('lane:')
  const fromState = card.columnId.startsWith('state:')
  if (fromLane) {
    if (isLaneColumn(column) && column.lane) return { kind: 'queue', lane: column.lane }
    if (column.id === initialStateColumn(columns)?.id) return { kind: 'submit' }
    return null
  }
  if (fromState && column.id !== card.columnId) {
    if (isStateColumn(column) && column.state) return { kind: 'transition', toState: column.state }
    if (column.kind === 'done') {
      const states = column.terminalStates ?? []
      if (states.length === 1) return { kind: 'transition', toState: states[0]!.name }
      if (states.length > 1) return { kind: 'finish', states }
    }
  }
  return null
}
